
import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, CheckCircle, Clock, Phone, ChevronRight, ChevronLeft, Info, Mic, ArrowRight } from "lucide-react";
import { SymptomIntake } from "./SymptomIntake";
import { TriageAssessment } from "./TriageAssessment";
import { TriageResult } from "./TriageResult";
import { useTriageAssessment } from "@/hooks/useTriageAssessment";
import { TriageData, TriageOutcome } from "@/types/triage";
import { useAuth } from "@/hooks/useAuth";
import { CollapsibleCard } from "@/components/ui/collapsible-card";
import { HealthSnapshot } from "@/components/patient/HealthSnapshot";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useNavigate } from "react-router-dom";

export function TriageForm() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 3; // Define total steps as a constant for easier reference
  const [triageData, setTriageData] = useState<TriageData>({
    symptoms: [],
    severity: '',
    duration: '',
    onset: '',
    additionalInfo: '',
    vitals: {},
    medicalHistory: [],
    medications: [],
    allergies: []
  });
  
  const { assessTriage, isAssessing } = useTriageAssessment();
  const [triageResult, setTriageResult] = useState<TriageOutcome | null>(null);
  const [savedTriageId, setSavedTriageId] = useState<string | undefined>(undefined);
  
  // Mock health data for the snapshot - in a real app this would come from the patient's record
  const mockHealthData = {
    bloodPressure: triageData.vitals.bloodPressure || "120/80",
    heartRate: triageData.vitals.heartRate || "72 bpm",
    temperature: triageData.vitals.temperature ? `${triageData.vitals.temperature}°F` : "98.6°F",
    weight: "165 lbs",
    healthScore: 85,
    lastUpdated: new Date().toISOString().split('T')[0]
  };
  
  // Set the patient ID in the triage data when the profile is available
  useEffect(() => {
    if (profile?.id) {
      console.log('[TriageForm] Setting patient ID in triage data:', profile.id);
      setTriageData(prev => ({
        ...prev,
        patientId: profile.role === 'patient' ? profile.id : prev.patientId
      }));
    }
  }, [profile]);

  const handleSymptomComplete = (data: Partial<TriageData>) => {
    setTriageData(prev => ({ 
      ...prev, 
      ...data,
      patientId: prev.patientId // Explicitly preserve patientId
    }));
    setCurrentStep(2);
  };

  const handleAssessmentComplete = async (data: Partial<TriageData>) => {
    const completeData = { 
      ...triageData, 
      ...data,
      patientId: triageData.patientId // Explicitly preserve patientId
    };
    setTriageData(completeData);
    
    // Perform triage assessment
    const result = await assessTriage(completeData);
    setTriageResult(result);
    
    // Store the saved triage ID for audit logging
    if (result && 'savedTriageId' in result) {
      console.log('Setting saved triage ID:', result.savedTriageId);
      setSavedTriageId(result.savedTriageId);
    }
    
    setCurrentStep(3);
  };

  const getStepIcon = (step: number) => {
    if (step < currentStep) return <CheckCircle className="w-5 h-5 text-green-600" />;
    if (step === currentStep) return <Clock className="w-5 h-5 text-blue-600" />;
    return <div className="w-5 h-5 rounded-full border-2 border-gray-300" />;
  };

  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'immediate': return 'bg-red-500';
      case 'urgent': return 'bg-orange-500';
      case 'standard': return 'bg-yellow-500';
      case 'non-urgent': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    // Only if we're not in an input field
    if (!(e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLSelectElement)) {
      if (e.key === 'ArrowRight' && currentStep < totalSteps) {
        // Don't auto-advance past step 2 as it requires form submission
        if (currentStep === 1) {
          // Only advance if step 1 has some data
          if (triageData.symptoms.length > 0) {
            setCurrentStep(2);
          }
        }
      } else if (e.key === 'ArrowLeft' && currentStep > 1 && currentStep < 3) {
        // Don't allow going back from results
        setCurrentStep(prev => prev - 1);
      }
    }
  }, [currentStep, totalSteps, triageData.symptoms.length]);

  // Add and remove keyboard event listeners
  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  // Function to navigate to audio triage
  const handleSwitchToAudioTriage = () => {
    navigate('/patient/audio-triage/welcome');
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Health Snapshot - Persistent header showing patient vitals */}
      <div className="sticky top-0 z-10 bg-white border border-gray-200 shadow-sm rounded-lg p-2 mb-2">
        <HealthSnapshot data={mockHealthData} variant="header" />
      </div>

      {/* Audio Triage Option Banner */}
      <Card className="border-2 border-indigo-200 bg-gradient-to-r from-indigo-50 to-purple-50">
        <CardContent className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="bg-indigo-100 p-3 rounded-full">
                <Mic className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <h3 className="font-semibold text-lg text-indigo-900">Try Our New Audio Triage</h3>
                <p className="text-indigo-700 max-w-md">
                  Simply tell us how you feel and get guided care recommendations faster.
                </p>
              </div>
            </div>
            <Button 
              onClick={handleSwitchToAudioTriage}
              className="bg-indigo-600 hover:bg-indigo-700 text-white flex items-center gap-2 whitespace-nowrap"
            >
              Switch to Audio Triage
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
      
      {/* Header with Legal Disclaimer */}
      <Card className="border-amber-200 bg-amber-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            Pre-Appointment Health Assessment
          </CardTitle>
          <CardDescription className="text-amber-800">
            <strong>Important:</strong> This assessment is for informational purposes only and does not replace professional medical advice. 
            If you're experiencing a medical emergency, call emergency services immediately.
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Progress Steps */}
      <CollapsibleCard 
        title="Triage Progress" 
        defaultOpen={true} 
        className="shadow-sm border-blue-100"
      >
        <CardHeader className="pb-2 pt-4">
          <CardTitle className="text-lg flex justify-between items-center">
            <span>Triage Progress</span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center text-sm text-blue-600 cursor-help">
                    <Info className="w-4 h-4 mr-1" />
                    <span>Keyboard shortcuts</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>← Previous step (Left Arrow)</p>
                  <p>→ Next step (Right Arrow)</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </CardTitle>
          <CardDescription>
            Step {currentStep} of {totalSteps}: {currentStep === 1 ? "Symptom Collection" : currentStep === 2 ? "Assessment" : "Results"}
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-2 pb-4 space-y-6">
          {/* Mobile step indicators - visible on small screens */}
          <div className="md:hidden flex justify-between items-center mb-2">
            {[1, 2, 3].map(step => (
              <div key={step} className="flex flex-col items-center">
                <div 
                  className={cn(
                    "rounded-full w-10 h-10 flex items-center justify-center mb-1",
                    currentStep > step 
                      ? "bg-green-100 text-green-700 border border-green-300" 
                      : currentStep === step 
                        ? "bg-blue-600 text-white" 
                        : "bg-gray-100 text-gray-400 border border-gray-200"
                  )}
                >
                  {currentStep > step ? <CheckCircle className="w-5 h-5" /> : step}
                </div>
                <span className="text-xs text-center">
                  {step === 1 ? "Symptoms" : step === 2 ? "Assessment" : "Results"}
                </span>
              </div>
            ))}
          </div>

          {/* Desktop step indicators - hidden on small screens */}
          <div className="hidden md:flex items-center justify-between relative mb-2">
            {[1, 2, 3].map((step, index) => (
              <div key={step} className="flex flex-col items-center z-10">
                <div 
                  className={cn(
                    "rounded-full w-12 h-12 flex items-center justify-center mb-2",
                    currentStep > step 
                      ? "bg-green-100 text-green-700 border border-green-300" 
                      : currentStep === step 
                        ? "bg-blue-600 text-white" 
                        : "bg-gray-100 text-gray-400 border border-gray-200"
                  )}
                >
                  {currentStep > step ? <CheckCircle className="w-6 h-6" /> : step}
                </div>
                <span className={cn(
                  "font-medium",
                  currentStep >= step ? "text-gray-900" : "text-gray-400"
                )}>
                  {step === 1 ? "Symptom Collection" : step === 2 ? "Assessment" : "Results"}
                </span>
              </div>
            ))}
            
            {/* Connecting lines */}
            <div className="absolute top-6 left-0 w-full h-0.5 bg-gray-200 -z-0">
              <div 
                className="h-full bg-blue-600 transition-all duration-300"
                style={{ width: `${((currentStep - 1) / (totalSteps - 1)) * 100}%` }}
              />
            </div>
          </div>
          
          {/* Progress bar */}
          <Progress 
            value={(currentStep / totalSteps) * 100} 
            className="h-3 rounded-full" 
            aria-label={`Triage progress: step ${currentStep} of ${totalSteps}`}
          />
        </CardContent>
      </CollapsibleCard>

      {/* Emergency Banner */}
      {triageResult?.urgency === 'immediate' && (
        <Card className="border-red-500 bg-red-50 shadow-md">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-full bg-red-100 p-2 flex-shrink-0">
                <Phone className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="font-bold text-red-900 text-lg mb-1">Seek Immediate Medical Attention</h3>
                <p className="text-red-800">
                  Based on your symptoms, you should contact emergency services or go to the nearest emergency department immediately.
                </p>
                <div className="mt-3">
                  <Button className="bg-red-600 hover:bg-red-700 text-white font-medium">
                    Call Emergency Services
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step Content */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        {currentStep === 1 && (
          <SymptomIntake 
            onComplete={handleSymptomComplete}
            initialData={triageData}
          />
        )}

        {currentStep === 2 && (
          <TriageAssessment 
            onComplete={handleAssessmentComplete}
            initialData={triageData}
            isLoading={isAssessing}
          />
        )}

        {currentStep === 3 && triageResult && (
          <TriageResult 
            result={triageResult}
            triageData={triageData}
            triageId={savedTriageId}
            patientId={triageData.patientId}
          />
        )}
      </div>

      {/* Bottom Actions */}
      {currentStep > 1 && currentStep < 3 && (
        <div className="flex justify-between items-center bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
          <Button 
            variant="outline" 
            onClick={() => setCurrentStep(prev => prev - 1)}
            className="flex items-center gap-2"
            aria-label="Go to previous step"
          >
            <ChevronLeft className="w-4 h-4" />
            Previous
          </Button>
          <div className="flex items-center gap-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="text-sm font-medium bg-blue-50 text-blue-800 px-3 py-1 rounded-full flex items-center cursor-help">
                    <span>Step {currentStep} of {totalSteps}</span>
                    <Info className="w-4 h-4 ml-1 text-blue-600" />
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Use keyboard shortcuts: ← → to navigate</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      )}
    </div>
  );
}
